import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV, waLink } from "@/data/site";
import { useQuote } from "@/components/QuoteDialog";

const WhatsAppIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.22 3.08.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35zM12 2a10 10 0 00-8.6 15.06L2 22l5.06-1.33A10 10 0 1012 2z"/>
  </svg>
);

const Header = () => {
  const { openQuote } = useQuote();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  const dark = !scrolled;

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-white/80 backdrop-blur-xl border-b border-zinc-200/60" : "bg-transparent"}`}
      data-testid="site-header">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 h-20 md:h-24 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center shrink-0" data-testid="logo-link">
          <img src={dark ? "/logo-axal-white.png" : "/logo-axal.png"} alt="AXAL Logistics"
            className="h-14 md:h-20 w-auto transition-opacity duration-300" />
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.to}`}
              className={`text-[14px] font-semibold transition-colors hover:text-[#FF5A00] ${pathname === n.to ? "text-[#FF5A00]" : dark ? "text-white/85" : "text-zinc-700"}`}>
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="header-whatsapp"
            className={`hidden sm:flex items-center gap-2 h-11 px-4 rounded-full border text-sm font-semibold transition-colors ${dark ? "border-white/25 text-white hover:border-[#25D366]" : "border-zinc-200 text-zinc-800 hover:border-[#25D366] hover:text-[#128C3E]"}`}>
            <WhatsAppIcon /> WhatsApp
          </a>
          <button onClick={() => openQuote()} data-testid="header-quote-button"
            className="h-11 px-4 md:px-5 rounded-full bg-[#FF5A00] text-white text-[13px] md:text-sm font-bold hover:bg-[#E04F00] transition-colors hover:-translate-y-0.5 duration-200">
            <span className="sm:hidden">Preventivo</span><span className="hidden sm:inline">Richiedi preventivo</span>
          </button>
          <button onClick={() => setOpen(true)} className={`xl:hidden p-2 transition-colors ${dark ? "text-white" : "text-zinc-800"}`} data-testid="mobile-menu-button" aria-label="Menu">
            <Menu size={26} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-[60] bg-[#121212] text-white p-6 xl:hidden"
            initial={{ opacity: 0, x: "100%" }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} data-testid="mobile-menu">
            <div className="flex items-center justify-between mb-10">
              <img src="/logo-axal-white.png" alt="AXAL Logistics" className="h-10 w-auto" />
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close"><X size={28} /></button>
            </div>
            <nav className="flex flex-col gap-1">
              {NAV.map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <Link to={n.to} className="block py-3 text-3xl font-extrabold font-head tracking-tight border-b border-white/10">
                    {n.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="mt-8 flex flex-col gap-3">
              <button onClick={() => { setOpen(false); openQuote(); }} className="h-13 py-4 rounded-full bg-[#FF5A00] font-bold">Richiedi preventivo</button>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" className="h-13 py-4 rounded-full border border-white/20 font-bold flex items-center justify-center gap-2"><WhatsAppIcon /> WhatsApp</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export { WhatsAppIcon };
export default Header;
